import { Suspense, useState } from "react"

import { ErrorBoundary, Loading, ResizableReorderable } from "web/components"

import {
  AssetsSummaryTile,
  ChartTileWrapper,
  ComparableDealSummaryTile,
  DealsSummaryTile,
  GalleryTile,
  MapTile,
  MapTileWrapper,
  MilestonesTile,
  ModelsSummaryTile,
  OverviewTableTileWrapper,
  PartnerSummaryTile,
  RichTextTileWrapper,
  SalesSummaryTile,
  StatisticsTile,
  SubjectSummaryTile,
  ViewableMarketingSummaryTile,
  ViewableSummaryTile,
  ViewableGalleryTile,
} from "web/components/Tiles"

export default function TileSet({
  canEdit = true,
  isTemplate,
  maxColumns = 3,
  onMoveTile,
  onResizeTile,
  photos = [],
  subjectType,
  subjectId,
  tiles,
  modifiedFromTemplate,
  overview,
}) {
  const [isDropzoneDisabled, setIsDropzoneDisabled] = useState(false)
  const [isDraggable, setIsDraggable] = useState(true)

  const components = {
    SaleAssetSaleSummaryTile: DealsSummaryTile,
    SaleAssetSummaryTile: SubjectSummaryTile,
    SaleAssetsSummaryTile: AssetsSummaryTile,
    SaleSummaryTile: SubjectSummaryTile,
    SalesSummaryTile: SalesSummaryTile,
    AssetsSummary: AssetsSummaryTile,
    ComparableDealSummary: ComparableDealSummaryTile,
    DealsSummary: DealsSummaryTile,
    Gallery: GalleryTile,
    Map: MapTile,
    MilestonesTile: MilestonesTile,
    StatisticsTile: StatisticsTile,
    ModelsSummary: ModelsSummaryTile,
    PartnerSummary: PartnerSummaryTile,
    SubjectSummary: SubjectSummaryTile,
    ViewableMap: MapTile,
    ViewableSummary: ViewableSummaryTile,
    ViewableMarketingSummary: ViewableMarketingSummaryTile,
    ViewableGallery: ViewableGalleryTile,
    ViewableParentSubjectSummary: SubjectSummaryTile,
  }

  function getTileFetchParams(tile) {
    const tileId = isTemplate ? tile.entryable_id : tile.index
    return {
      id: tileId,
      routeSuffix: tile.route_suffix,
      overviewKey: overview.key,
      parentKeys: overview.refreshKeys,
      width: tile.width,
    }
  }

  function getOverviewTile(tile) {
    // [TODO] This condition is temporary for backward compatibility
    const tileFetchParams = getTileFetchParams(tile)
    if (tile.type.match("Table")) {
      return (
        <OverviewTableTileWrapper
          canEdit={isTemplate || canEdit}
          isTemplate={isTemplate}
          key={tile.index}
          overview={overview}
          tileFetchParams={tileFetchParams}
        />
      )
    } else if (tile.type.match("RichText")) {
      return (
        <RichTextTileWrapper
          canEdit={isTemplate || canEdit}
          isTemplate={isTemplate}
          key={tile.index}
          overview={overview}
          setIsDraggable={setIsDraggable}
          tileHeight={tile.height}
          tileFetchParams={tileFetchParams}
        />
      )
    } else if (tile.type.match("Chart")) {
      return (
        <ChartTileWrapper
          canEdit={isTemplate || canEdit}
          isTemplate={isTemplate}
          key={tile.index}
          overview={overview}
          tileFetchParams={tileFetchParams}
        />
      )
    } else if (tile.type.match("Map")) {
      return (
        <MapTileWrapper
          canEdit={isTemplate || canEdit}
          isTemplate={isTemplate}
          key={tile.index}
          overview={overview}
          tileFetchParams={tileFetchParams}
          subjectType={subjectType}
        />
      )
    } else {
      const TileComponent = components[tile.type]
      return (
        <TileComponent
          canEdit={isTemplate || canEdit}
          isDropzoneDisabled={isDropzoneDisabled}
          isTemplate={isTemplate}
          key={tile.index}
          overview={overview}
          photos={photos}
          subjectType={subjectType}
          subjectId={subjectId}
          modifiedFromTemplate={modifiedFromTemplate}
          tileFetchParams={tileFetchParams}
          isDoubleColumn={tile?.width === 2}
        />
      )
    }
  }

  return (
    <ResizableReorderable
      items={tiles}
      rowHeightPx={396}
      maxItemHeight={4}
      maxItemWidth={maxColumns}
      pageWidth={maxColumns}
      pageHeight={4}
      onResizeItem={onResizeTile}
      onMoveItem={onMoveTile}
      containerClasses="gap-y-5 gap-x-5"
      pageItemContainerClasses="bg-white rounded-md shadow group"
      disableDropzone={setIsDropzoneDisabled}
      isDraggable={isDraggable}
      canDragResize={false}
      canEdit={canEdit}
      showBottomBorder={true}
    >
      {tiles.map((tile) => (
        <ErrorBoundary key={`tile-${tile.index}`}>
          <Suspense fallback={<Loading inline />}>
            {getOverviewTile(tile)}
          </Suspense>
        </ErrorBoundary>
      ))}
    </ResizableReorderable>
  )
}
